import { gql, useQuery } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
  Spinner,
  Switch,
  Text,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import * as Yup from 'yup';

import * as orgRoutes from 'src/app/account/routes';
import { baseGraphSetupValidationSchema } from 'src/app/graph/shared/graphSetupHelpers';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { GraphIDAvailableQuery } from 'src/hooks/useIsGraphIdAvailable';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { createUniqueName } from 'src/lib/createUniqueName';
import { generateGraphId } from 'src/lib/graphOnboarding/helpers/utils';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { generateUniqueString } from '../../lib/generateUniqueString';
import { Form } from '../common/form/Form';
import { ErrorMessage } from '../errorMessage/ErrorMessage';

import { getCanLoggedInUserCreateHiddenGraphOnAccount } from './canLoggedInUserCreateHiddenGraph';
import { PublishArch, useCreateGraph } from './useCreateGraph';

const graphSetupValidationSchema = baseGraphSetupValidationSchema.concat(
  Yup.object().shape({
    hiddenFromUninvitedNonAdminAccountMembers: Yup.boolean().required(),
    publishArch: Yup.string(),
  }),
);

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof graphSetupValidationSchema>;

interface Props {
  accountId: string;
  onClose: () => void;
}

export const GraphSetupModal = ({ accountId, onClose }: Props) => {
  const { createGraph, createGraphError } = useCreateGraph();
  const history = useHistory();

  const { data, loading } = useQuery(gql<
    GraphQLTypes.GraphSetupModalQuery,
    GraphQLTypes.GraphSetupModalQueryVariables
  >`
    query GraphSetupModalQuery {
      me {
        id
        ... on User {
          id
          memberships {
            account {
              id
              name
              roles {
                canCreateService
              }
              services {
                id
                title
              }
            }
            user {
              id
            }
          }
        }
      }
    }
  `);

  const memberships = React.useMemo(
    () => (data?.me?.__typename === 'User' ? data.me.memberships : undefined),
    [data],
  );

  const accountOptions = memberships
    ?.filter(({ account }) => account.roles?.canCreateService)
    .map(({ account }) => ({ name: account.name, key: account.id }));

  const defaultGraphTitle = React.useMemo(() => {
    const existingGraphTitles = memberships?.flatMap(({ account }) =>
      account.services.map(({ title }) => title),
    );
    return createUniqueName('My Graph', existingGraphTitles ?? []);
  }, [memberships]);

  const trackCustomEvent = useTrackCustomEvent();

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormik<FormValues>({
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      graphTitle: defaultGraphTitle,
      graphId: generateGraphId(defaultGraphTitle),
      hiddenFromUninvitedNonAdminAccountMembers: false,
      accountId,
      publishArch: 'federation',
    },
    async onSubmit(formValues) {
      const result = await createGraph({
        accountId: formValues.accountId,
        graphId: formValues.graphId,
        graphTitle: formValues.graphTitle,
        hiddenFromUninvitedNonAdminAccountMembers:
          formValues.hiddenFromUninvitedNonAdminAccountMembers,
        publishArch: formValues.publishArch,
      });
      if (result) {
        history.push(
          orgRoutes.graphs.location({
            orgId: formValues.accountId,
            overlay: 'publish-your-schema',
            graphId: formValues.graphId,
            publishArch: formValues.publishArch as PublishArch,
          }),
        );
        // fire ga4 event
        trackCustomEvent({
          action: 'user_action',
          // Old event, this style of category is deprecated
          category: 'Added a graph' as EventCategory,
          label: 'User actions',
          legacyValueDimension: 'deployed',
        });
      }
    },
    validationSchema: graphSetupValidationSchema,
  });

  const [showGraphIdField, setShowGraphIdField] = useState(false);
  const graphIdAvailable = useQuery<
    GraphQLTypes.GraphIDAvailableQuery,
    GraphQLTypes.GraphIDAvailableQueryVariables
  >(GraphIDAvailableQuery, {
    variables: {
      accountId: values.accountId,
      graphId: values.graphId,
    },
  });

  const currentPlan = useCurrentPlan({
    skip: !values.accountId,
    accountId: values.accountId,
  });
  // store a unique string to add as a suffix so it doesn't regenerate when typing
  const [uniqueString, setUniqueString] = useState(generateUniqueString);

  // We want to keep the graph id in sync with graph title if the user hasn't edited yet.
  useEffect(() => {
    if (!touched.graphId) {
      setFieldValue('graphId', generateGraphId(values.graphTitle));
    }
  }, [setFieldValue, touched.graphId, values.graphTitle]);

  // If the user hasn't edited the id, we want to update the generate id so it's unique
  useEffect(() => {
    if (
      !graphIdAvailable.loading &&
      !graphIdAvailable.data?.account?.graphIDAvailable &&
      !touched.graphId
    ) {
      let nextId = generateGraphId(values.graphTitle, uniqueString);
      // This will catch if the unique string has already been used with this title and generate a new one
      if (nextId === values.graphId) {
        const nextUniqueString = generateUniqueString();
        setUniqueString(nextUniqueString);
        nextId = generateGraphId(values.graphTitle, nextUniqueString);
      }
      setFieldValue('graphId', nextId);
    }
  }, [
    setFieldValue,
    touched.graphId,
    values.graphTitle,
    uniqueString,
    values.graphId,
    graphIdAvailable.loading,
    graphIdAvailable.data?.account?.graphIDAvailable,
  ]);

  const canLoggedInUserCreateHiddenGraph =
    !!data &&
    getCanLoggedInUserCreateHiddenGraphOnAccount(
      data,
      currentPlan.currentPlan?.userRoles ?? false,
      {
        accountId: values.accountId,
      },
    );

  if (!loading && !accountOptions?.length) {
    return (
      <Modal isOpen onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Cannot create Graph</ModalTitle>
          </ModalHeader>
          <ModalBody>
            You do not have permission to create a graph in any of your
            organizations.
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} type="button">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  const graphTitleError = touched.graphTitle && errors.graphTitle;
  const graphIdError = touched.graphId && errors.graphId;

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Form onSubmit={handleSubmit} noValidate>
          <ModalHeader data-testid="graph-setup-modal-header">
            <ModalTitle>Let's set up your graph</ModalTitle>
            <ModalDescription>
              Create a graph to start running operations, schema checks, and
              seeing usage information.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <div className="flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                <FormControl isInvalid={!!graphTitleError} isRequired>
                  <div className="flex justify-between">
                    <FormLabel>Graph title</FormLabel>
                    <span className="text-code-sm mx-1 inline-block truncate text-right text-secondary">
                      Graph ID: {values.graphId}{' '}
                      <Tooltip
                        label={
                          showGraphIdField ? 'Hide graph ID' : 'Edit graph ID'
                        }
                      >
                        <IconButton
                          aria-label={
                            showGraphIdField ? 'Hide graph ID' : 'Edit graph ID'
                          }
                          onClick={() => setShowGraphIdField((v) => !v)}
                          variant="hidden"
                          size="xs"
                        >
                          <IconEdit />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </div>
                  <Input
                    autoFocus={true}
                    isDisabled={isSubmitting}
                    name="graphTitle"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="e.g., my-graph"
                    value={values.graphTitle}
                    type="text"
                  />
                  <FormErrorMessage>{graphTitleError}</FormErrorMessage>
                </FormControl>

                {showGraphIdField && (
                  <FormControl isInvalid={!!graphIdError} isRequired>
                    <FormLabel>Graph ID</FormLabel>
                    <Input
                      isDisabled={isSubmitting}
                      name="graphId"
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldTouched('graphId', true);
                        handleChange(event);
                      }}
                      value={values.graphId}
                      type="text"
                      rightElement={
                        <Tooltip
                          label={
                            graphIdAvailable.loading
                              ? 'Loading graph ID availability'
                              : graphIdAvailable.data?.account?.graphIDAvailable
                                ? 'Graph ID available'
                                : 'This graph ID is already taken '
                          }
                        >
                          {graphIdAvailable.loading ? (
                            <Spinner size="tiny" />
                          ) : graphIdAvailable.data?.account
                              ?.graphIDAvailable ? (
                            <IconCheck className="rounded-full text-icon-success" />
                          ) : (
                            <IconClose className="cursor-pointer rounded-full text-icon-error" />
                          )}
                        </Tooltip>
                      }
                    />
                    <FormErrorMessage>{graphIdError}</FormErrorMessage>
                  </FormControl>
                )}
                <FormControl isRequired>
                  <FormLabel>
                    Graph architecture
                    <Tooltip
                      interactive
                      label={
                        <>
                          Whether this project represents a federated supergraph
                          (composed of underlying subgraphs), or a single
                          (monolithic) GraphQL server.{' '}
                        </>
                      }
                    >
                      <IconInfo className="text-icon-secondary" />
                    </Tooltip>
                  </FormLabel>
                  <Select
                    value={values.publishArch}
                    onChange={(e) => {
                      setFieldValue('publishArch', e.target.value);
                    }}
                  >
                    <option value="federation">Supergraph (default)</option>
                    <option value="monolith">Monolith</option>
                  </Select>
                </FormControl>

                {canLoggedInUserCreateHiddenGraph && (
                  <FormControl>
                    <FormLabel className="flex items-center justify-between">
                      Visibility
                      <Switch
                        isChecked={
                          !values.hiddenFromUninvitedNonAdminAccountMembers
                        }
                        onChange={() =>
                          setFieldValue(
                            'hiddenFromUninvitedNonAdminAccountMembers',
                            !values.hiddenFromUninvitedNonAdminAccountMembers,
                          )
                        }
                      >
                        {values.hiddenFromUninvitedNonAdminAccountMembers
                          ? 'Hidden'
                          : 'Visible'}
                      </Switch>
                    </FormLabel>
                    <Text size="base">
                      {values.hiddenFromUninvitedNonAdminAccountMembers
                        ? 'Your graph will be hidden from other organization members unless you add them.'
                        : 'Your graph will be visible to other members in your organization.'}
                    </Text>
                  </FormControl>
                )}

                {createGraphError && (
                  <ErrorMessage className="mb-10" error={createGraphError} />
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                isDisabled={isSubmitting}
                variant="secondary"
                onClick={async () => {
                  ampli.cancelConnectApiClicked({
                    'API Type': 'GraphQL',
                    OnboardingVariant: 'GraphOS-Trial',
                  });
                  onClose();
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button
                isLoading={isSubmitting}
                loadingText="Submitting"
                type="submit"
                onClick={() => {
                  ampli.proceedToPublishSchemaClicked({
                    'API Type': 'GraphQL',
                    OnboardingVariant: 'GraphOS-Trial',
                  });
                }}
                isDisabled={Boolean(
                  graphTitleError ||
                    graphIdError ||
                    graphIdAvailable.loading ||
                    !graphIdAvailable.data?.account?.graphIDAvailable,
                )}
              >
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
